import './styles.scss'
import { Grid, BarGraph } from '@/components'
import { isDecimal } from '@/utils/number'
import {
  ThermostatCostSavingsViewModel,
  ThermostatCostSavingsDataPeriod,
} from '../useThermostatCostSavingsViewModel'
import ThermostatSummaryCard from './ThermostatSummaryCard'
import formatClasses from '@/utils/classes/formatClasses'
import getColor from '@/utils/color/getColor'
import dayjs from 'dayjs'
import { useState, useEffect, useMemo } from 'react'
import { positiveClamp } from '@/utils/limit'
import { DateRepresentation } from '@/types/dates'

export interface SavingsDataPoint {
  savings: number // in dollars, negative = cost, positive = savings
  kwhUsed: number // kWh from optiwatt adjustments
  label: string
  maxValue: number // absolute max for scaling
  minValue: number // absolute min for scaling (negative)
}

export interface HoveredDataPointInfo {
  savings: number
  kwhUsed: number
  period: string
}

function generateLabel(
  date: DateRepresentation,
  period: ThermostatCostSavingsDataPeriod,
) {
  if (period !== 'day') return dayjs(date as string).format('ddd, MMM D')
  else {
    const nextHour = dayjs(date as string)
      .add(1, 'hour')
      .format()
    return `${dayjs(date as string).format('h')}–${dayjs(nextHour).format(
      'ha',
    )}`
  }
}

interface SavingsDataPointProps extends SavingsDataPoint {
  period: ThermostatCostSavingsDataPeriod
  selected: boolean
  onPointerEnter: (info: HoveredDataPointInfo) => void
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
  setSelected: (selected: boolean) => void
}

function SavingsDataPoint(props: SavingsDataPointProps) {
  const classes = {
    bar: formatClasses(['total-spent-bar-graph-bar']),
    dataPoint: formatClasses([
      'total-spent-data-point',
      props.selected ? 'selected' : undefined,
    ]),
  }

  const BAR_GRAPH_HEIGHT = 105 // Height in pixels (same as ThermostatCostsBarGraph)
  const ZERO_OFFSET_PX = BAR_GRAPH_HEIGHT / 2 // Zero line at middle

  // Calculate bar height and position
  const absValue = Math.abs(props.savings)
  const maxAbsValue = Math.max(
    Math.abs(props.maxValue),
    Math.abs(props.minValue),
  )
  const heightPercentage = maxAbsValue > 0 ? absValue / maxAbsValue : 0
  // Calculate height in pixels - half of total since zero is in middle
  const barHeightPx = Math.max(
    Math.round((heightPercentage * BAR_GRAPH_HEIGHT) / 2),
    props.savings === 0 ? 0 : 4, // Minimum 4px if there's a value
  )
  const height = `${barHeightPx}px`

  // Position: above zero for positive, below zero for negative
  const isPositive = props.savings > 0
  const positionStyle = isPositive
    ? {
        bottom: `${ZERO_OFFSET_PX}px`,
        top: 'auto',
        height: height,
      }
    : {
        top: `${ZERO_OFFSET_PX}px`,
        bottom: 'auto',
        height: height,
      }

  function getBackground() {
    // Treat very small values (filler data) as zero for grey background
    if (props.savings === 0 || Math.abs(props.savings) <= 0.01) {
      return getColor('grey-200')
    }
    return isPositive ? getColor('green-700') : getColor('red-500')
  }

  function onPointerEnter() {
    props.setSelected(true)

    const label = props.label
      ? props.label.includes('between') || props.period !== 'day'
        ? props.label
        : `Today between ${props.label}`
      : ''

    props.onPointerEnter({
      savings: props.savings,
      kwhUsed: props.kwhUsed,
      period: label,
    })
  }

  return (
    <div
      className={classes.dataPoint}
      data-key={props.label ? props.label : undefined}
      data-type={props.period}
      onPointerDown={props.onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={() => props.setSelected(false)}
    >
      <div
        className={classes.bar}
        style={{
          background: getBackground(),
          ...positionStyle,
        }}
      />
    </div>
  )
}

function onPointerDown(event: React.PointerEvent<HTMLElement>) {
  // @ts-expect-error hasPointerCapture does not exist on event.target
  if (event.target.hasPointerCapture(event.pointerId)) {
    // @ts-expect-error releasePointerCapture does not exist on event.target
    event.target.releasePointerCapture(event.pointerId)
  }
}

export default function ThermostatSavingsBarGraph(
  props: ThermostatCostSavingsViewModel,
) {
  // Extract and transform data from usageIntervals
  const dataPoints = useMemo(() => {
    const points: SavingsDataPoint[] = props.usageIntervals.map((interval) => {
      const costCents = interval.usage.optiwatt_adjustments?.cost_cents || 0
      const kwhUsed = interval.usage.optiwatt_adjustments?.kwh_used || 0
      // Convert to dollars and negate (negative cost = positive savings)
      const savings = -(costCents / 100)
      const date = interval.date as DateRepresentation
      const label = generateLabel(date, props.period)

      return {
        savings,
        kwhUsed,
        label,
        maxValue: 0, // Will be calculated below
        minValue: 0, // Will be calculated below
      }
    })

    // Calculate max and min values for scaling
    const savingsValues = points.map((p) => p.savings)
    const maxValue = savingsValues.length > 0 ? Math.max(...savingsValues) : 1
    const minValue = savingsValues.length > 0 ? Math.min(...savingsValues) : -1
    const absMax = Math.max(Math.abs(maxValue), Math.abs(minValue))

    // Round up to a nice round number for display and scaling
    // Use smaller increments for better accuracy
    let roundedMax: number
    if (absMax === 0) {
      roundedMax = 0.1 // Default to 0.1 for empty/zero data
    } else if (absMax <= 0.05) {
      roundedMax = 0.05
    } else if (absMax <= 0.1) {
      roundedMax = 0.1
    } else if (absMax <= 0.25) {
      roundedMax = 0.25
    } else if (absMax <= 0.5) {
      roundedMax = 0.5
    } else if (absMax <= 1) {
      roundedMax = 1
    } else if (absMax <= 2) {
      roundedMax = 2
    } else {
      // Round up to nearest 0.5
      roundedMax = Math.ceil(absMax * 2) / 2
    }

    // Update all points with max/min values (use rounded max for consistent scaling)
    return points.map((point) => ({
      ...point,
      maxValue: roundedMax,
      minValue: -roundedMax,
    }))
  }, [props.usageIntervals, props.period])

  const [selectedBar, setSelectedBar] = useState<number | null>(null)
  const [selectedInfo, setSelectedInfo] = useState<HoveredDataPointInfo>({
    savings: dataPoints.reduce((sum, point) => sum + point.savings, 0) || 0,
    kwhUsed: dataPoints.reduce((sum, point) => sum + point.kwhUsed, 0) || 0,
    period: props.periodLabel,
  })

  const clampedSavings = positiveClamp(Math.abs(selectedInfo.savings))
  const savingsDecimals = isDecimal(clampedSavings) ? 2 : 0

  const clampedKwhUsed = Math.abs(selectedInfo.kwhUsed)
  const kwhUsedDecimals = clampedKwhUsed === 0 || clampedKwhUsed > 100 ? 0 : 1
  const energyLabel = selectedInfo.kwhUsed < 0 ? 'Energy Saved' : 'Energy Used'

  // Calculate x-axis and y-axis labels
  const xAxis = useMemo(() => {
    if (dataPoints.length === 0) {
      // Default labels based on period
      if (props.period === 'day') return ['12a', 'noon', '12a']
      if (props.period === 'week') return ['Wed', 'Thu', 'Thu']
      return ['1st', '15th', '30th']
    }

    if (dataPoints.length <= 3) {
      // Show all labels if 3 or fewer
      return dataPoints.map((p, index) => {
        const date = props.usageIntervals[index]?.date as DateRepresentation
        if (!date) return ''

        if (props.period === 'day') {
          // Format as 12a, 1a, 2a, etc. or 12p, 1p, 2p, etc.
          const hour = dayjs(date as string).hour()
          if (hour === 0) return '12a'
          if (hour === 12) return '12p'
          if (hour < 12) return `${hour}a`
          return `${hour - 12}p`
        } else if (props.period === 'month') {
          // Format as ordinal: 1st, 2nd, 3rd, etc.
          return dayjs(date as string).format('Do')
        } else {
          // Week: use day abbreviation
          return dayjs(date as string).format('ddd')
        }
      })
    } else {
      // Show first, middle, last
      const first = dataPoints[0]
      const middle = dataPoints[Math.floor(dataPoints.length / 2)]
      const last = dataPoints[dataPoints.length - 1]
      const labels: (string | number)[] = []

      const firstDate = props.usageIntervals[0]?.date as DateRepresentation
      const middleDate = props.usageIntervals[Math.floor(dataPoints.length / 2)]
        ?.date as DateRepresentation
      const lastDate = props.usageIntervals[dataPoints.length - 1]
        ?.date as DateRepresentation

      if (first && firstDate) {
        if (props.period === 'day') {
          const hour = dayjs(firstDate as string).hour()
          if (hour === 0) labels.push('12a')
          else if (hour === 12) labels.push('12p')
          else if (hour < 12) labels.push(`${hour}a`)
          else labels.push(`${hour - 12}p`)
        } else if (props.period === 'month') {
          labels.push(dayjs(firstDate as string).format('Do'))
        } else {
          labels.push(dayjs(firstDate as string).format('ddd'))
        }
      }

      if (middle && middle !== first && middle !== last && middleDate) {
        if (props.period === 'day') {
          labels.push('noon')
        } else if (props.period === 'month') {
          labels.push(dayjs(middleDate as string).format('Do'))
        } else {
          labels.push(dayjs(middleDate as string).format('ddd'))
        }
      } else if (props.period === 'day') {
        labels.push('noon')
      }

      if (last && last !== first && lastDate) {
        if (props.period === 'day') {
          const hour = dayjs(lastDate as string).hour()
          if (hour === 0) labels.push('12a')
          else if (hour === 12) labels.push('12p')
          else if (hour < 12) labels.push(`${hour}a`)
          else labels.push(`${hour - 12}p`)
        } else if (props.period === 'month') {
          labels.push(dayjs(lastDate as string).format('Do'))
        } else {
          labels.push(dayjs(lastDate as string).format('ddd'))
        }
      }

      // Fallback defaults
      if (labels.length === 0) {
        if (props.period === 'day') return ['12a', 'noon', '12a']
        if (props.period === 'week') return ['Wed', 'Thu', 'Thu']
        return ['1st', '15th', '30th']
      }

      return labels
    }
  }, [dataPoints, props.period, props.usageIntervals])

  const yAxis = useMemo(() => {
    if (dataPoints.length === 0) return ['$1', '$0.5', '$0']
    // Use the maxValue from dataPoints which already has the rounded value
    const maxValue = dataPoints[0]?.maxValue || 1

    // Create y-axis labels: top (positive), middle (zero), bottom (negative)
    // Format based on the value size
    const formatValue = (val: number) => {
      if (val >= 1) {
        return `$${val}`
      } else if (val >= 0.1) {
        // Show 2 decimal places for values < 1, but remove trailing zeros
        const formatted = val.toFixed(2)
        return formatted.endsWith('0')
          ? `$${parseFloat(formatted)}`
          : `$${formatted}`
      } else {
        return `$${val.toFixed(2)}`
      }
    }

    const topLabel = formatValue(maxValue)
    const midLabel = '$0'
    const bottomLabel = `-${formatValue(maxValue)}`

    return [topLabel, midLabel, bottomLabel]
  }, [dataPoints])

  useEffect(() => {
    setSelectedInfo({
      savings: dataPoints.reduce((sum, point) => sum + point.savings, 0) || 0,
      kwhUsed: dataPoints.reduce((sum, point) => sum + point.kwhUsed, 0) || 0,
      period: props.periodLabel,
    })
    setSelectedBar(null)
  }, [props.period, props.periodLabel, dataPoints])

  function onPointerEnter(info: HoveredDataPointInfo) {
    setSelectedInfo(info)
  }

  function onPointerLeave() {
    setSelectedInfo({
      savings: dataPoints.reduce((sum, point) => sum + point.savings, 0) || 0,
      kwhUsed: dataPoints.reduce((sum, point) => sum + point.kwhUsed, 0) || 0,
      period: props.periodLabel,
    })
    setSelectedBar(null)
  }

  function DataPoints() {
    // Only compute filler data when loading className is set and there is no data
    const shouldShowFillerData = props.loading && dataPoints.length === 0

    // Generate filler data points for loading animation when no data exists
    const getFillerDataPoints = (): SavingsDataPoint[] => {
      if (!shouldShowFillerData) return []

      const count =
        props.period === 'day' ? 24 : props.period === 'week' ? 7 : 30
      // Use small positive values so bars animate upward from zero line
      return Array.from({ length: count }, () => ({
        savings: 0.01, // Small positive value for upward animation
        kwhUsed: 0,
        label: '',
        maxValue: 0.1, // Default max for empty data
        minValue: -0.1, // Default min for empty data
      }))
    }

    const pointsToRender = shouldShowFillerData
      ? getFillerDataPoints()
      : dataPoints

    return (
      <>
        {pointsToRender.map((item, index) => (
          <SavingsDataPoint
            key={index}
            {...item}
            period={props.period}
            selected={index === selectedBar}
            onPointerEnter={onPointerEnter}
            onPointerDown={onPointerDown}
            setSelected={(selected: boolean) => {
              if (!selected) {
                setSelectedInfo({
                  savings:
                    dataPoints.reduce((sum, point) => sum + point.savings, 0) ||
                    0,
                  kwhUsed:
                    dataPoints.reduce((sum, point) => sum + point.kwhUsed, 0) ||
                    0,
                  period: props.periodLabel,
                })
                setSelectedBar(null)
              } else {
                setSelectedBar(index)
              }
            }}
          />
        ))}
      </>
    )
  }

  return (
    <>
      <Grid flow="row" gap="24px">
        <ThermostatSummaryCard
          period={selectedInfo.period}
          tagClassName="bg-themed-grey-100 rounded-lg p-4"
          loading={props.loading}
          left={{
            label: energyLabel,
            value: clampedKwhUsed,
            decimals: kwhUsedDecimals,
            unit: 'kWh',
          }}
          right={{
            label: 'Total Saved',
            value: selectedInfo.savings,
            decimals: savingsDecimals,
            unit: '$',
            unitPosition: 'prefix',
            valueColor:
              selectedInfo.savings < 0
                ? 'text-themed-red-500'
                : 'text-themed-green-700',
          }}
        />
        <BarGraph
          className={props.loading ? 'loading' : undefined}
          onPointerLeave={onPointerLeave}
          xAxis={xAxis}
          yAxis={yAxis}
        >
          <DataPoints />
        </BarGraph>
      </Grid>
    </>
  )
}
